import { Text as BaconText, View, ViewProps, TextProps } from "@-/com.npmjs.bacons-react-views";
import { Text, TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import React, {
  Children,
  ComponentType,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  createContext,
  isValidElement,
  useContext,
  useMemo,
} from "react";
import { StyleSheet, type ViewStyle } from "@-/com.npmjs.react-native";

// Context to track header cells for proper rendering
const TableHeaderContext = createContext(false);

// `components` is the MDX component-map prop `@bacons/mdx` threads through the table
// elements a GFM pipe table compiles to. Nothing here uses it, so it's
// destructured-and-ignored below — typed as the component map rather than `any` to
// satisfy the no-explicit-any lint rule.
type TableProps = ViewProps & { components?: Record<string, ComponentType> };
type CellProps = TextProps & { components?: Record<string, ComponentType> };

// Cell text renders through the project `Text` (not Bacon's RN-web `Text`) so a
// cell's plain string children and any nested `<Span>` share identical font
// metrics — otherwise the two Text implementations drift on line-height/baseline.
// The cell's text class is published via `TextClassContext` so a nested `<Span>`
// inherits it (and only overrides color), keeping the colored run aligned with
// the surrounding text. `text-sm` matches Bacon's original ~14px cell default.
const TH_TEXT_CLASS = "text-sm font-semibold";
const TD_TEXT_CLASS = "text-sm";

// `@bacons/mdx`'s original table styling was a fixed light-theme palette (gray
// borders, near-white header, mid-gray body text). On a dark background the
// header text turned white-on-near-white and disappeared. Resolve every table
// color from the uniwind theme tokens at render time instead, so the grid tracks
// light/dark mode like the rest of the app.
type TableColors = {
  /** Body cell + caption text. */
  bodyText: string | undefined;
  /** Outer table border. */
  border: string | undefined;
  /** Cell dividers and row separators (kept subtle, same token as the border). */
  divider: string | undefined;
  /** Header/footer cell background. */
  headerBackground: string | undefined;
  /** Header cell text. */
  headerText: string | undefined;
};

const FALLBACK_TABLE_COLORS: TableColors = {
  bodyText: undefined,
  border: undefined,
  divider: undefined,
  headerBackground: undefined,
  headerText: undefined,
};

// `Table` resolves the theme colors once and publishes them so every nested
// cell/row reads the same values without each re-running the hook. A bare
// `TH`/`TD` rendered outside a `Table` falls back to the (theme-`Text`) default.
const TableColorsContext = createContext<TableColors>(FALLBACK_TABLE_COLORS);

/** Theme colors from `useCSSVariable` may be HSL channels or resolved hex/rgb/hsl strings. */
function hslFromChannels(channels: string | number | undefined): string | undefined {
  if (channels === undefined || channels === "") {
    return undefined;
  }

  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }

  return `hsl(${value})`;
}

function useTableColors(): TableColors {
  const border = useCSSVariable("--color-border");
  const muted = useCSSVariable("--color-muted");
  const foreground = useCSSVariable("--color-foreground");
  const mutedForeground = useCSSVariable("--color-muted-foreground");

  return useMemo(
    () => ({
      bodyText: hslFromChannels(mutedForeground),
      border: hslFromChannels(border),
      divider: hslFromChannels(border),
      headerBackground: hslFromChannels(muted),
      headerText: hslFromChannels(foreground),
    }),
    [border, muted, foreground, mutedForeground],
  );
}

function CellText({
  children,
  className,
  color,
}: {
  children?: React.ReactNode;
  className: string;
  // Explicit cell text color resolved from the theme (header vs body). When
  // unset, the text falls back to the theme `Text` foreground.
  color?: string;
}) {
  return (
    <TextClassContext.Provider value={className}>
      <Text className={className} selectable style={color == null ? undefined : { color }}>
        {children}
      </Text>
    </TextClassContext.Provider>
  );
}

// Columns size to their text like an HTML table's automatic layout. Each column starts at its
// widest cell's one-line width and gives up width toward its longest word, never below it, so a
// word only breaks once the longest words alone overflow the table; spare width is shared in
// proportion to each column's text. Rows lay out independently, so every row's cells take sizes
// computed from the whole table's text. The widths are estimated from character counts at the
// cell font size rather than measured, so a slide's measuring copy and its page lay out alike.

/** A column's text in Latin-letter widths: its widest cell on one line, and its longest word. */
type TableColumnText = { longestWordUnits: number; textUnits: number };

/** Each column's flex sizing, by column index (see `styles` for why styles are `object`). */
const TableColumnStylesContext = createContext<readonly object[]>([]);
/** The index of the column a cell sits in, published by its `TR`. */
const TableColumnIndexContext = createContext(-1);

/** `text-sm` at the default type scale, for a table outside a slide's scoped text size. */
const DEFAULT_CELL_FONT_SIZE = 14;
/** Average Latin letter width in ems, rounded up so a longest word's floor holds. */
const LATIN_LETTER_WIDTH_EM = 0.6;
/** A cell's horizontal padding and right border (see `styles.cell`). */
const CELL_HORIZONTAL_INSET = 12 * 2 + 1;

const WHITESPACE_PATTERN = /\s/u;

/** A row's cells, the same list `getTableColumnText` indexes. */
function getRowCells(children: ReactNode): ReactElement[] {
  return Children.toArray(children).filter(isValidElement);
}

function getNodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((child) => getNodeText(child)).join("");
  }
  return isValidElement<{ children?: ReactNode }>(node) ? getNodeText(node.props.children) : "";
}

function getCharacterUnits(character: string): number {
  const codePoint = character.codePointAt(0) ?? 0;
  // Joiners and variation selectors combine with the emoji before them.
  if (codePoint === 0x20_0d || (codePoint >= 0xfe_00 && codePoint <= 0xfe_0f)) {
    return 0;
  }
  // Hangul, CJK and emoji are about two Latin letters wide.
  return codePoint >= 0x11_00 ? 2 : 1;
}

function getCellText(text: string): TableColumnText {
  let longestWordUnits = 0;
  let textUnits = 0;
  let wordUnits = 0;
  for (const character of text) {
    const units = getCharacterUnits(character);
    textUnits += units;
    if (WHITESPACE_PATTERN.test(character)) {
      wordUnits = 0;
    } else if (units === 2) {
      // A line may break on either side of a CJK character or an emoji.
      wordUnits = 0;
      longestWordUnits = Math.max(longestWordUnits, units);
    } else {
      wordUnits += units;
      longestWordUnits = Math.max(longestWordUnits, wordUnits);
    }
  }
  return { longestWordUnits, textUnits };
}

function getTableColumnText(children: ReactNode): TableColumnText[] {
  const columns: TableColumnText[] = [];
  const visit = (nodes: ReactNode) => {
    Children.forEach(nodes, (node) => {
      if (!isValidElement<{ children?: ReactNode }>(node) || node.type === Caption) {
        return;
      }
      if (node.type !== TR) {
        visit(node.props.children);
        return;
      }
      for (const [index, cell] of getRowCells(node.props.children).entries()) {
        const cellText = getCellText(getNodeText(cell).trim());
        columns[index] = {
          longestWordUnits: Math.max(
            columns[index]?.longestWordUnits ?? 0,
            cellText.longestWordUnits,
          ),
          textUnits: Math.max(columns[index]?.textUnits ?? 0, cellText.textUnits),
        };
      }
    });
  };
  visit(children);
  return columns;
}

function getColumnStyle({ longestWordUnits, textUnits }: TableColumnText, unitWidth: number) {
  const flexBasis = CELL_HORIZONTAL_INSET + textUnits * unitWidth;
  return {
    flexBasis,
    flexGrow: textUnits,
    // Yoga shrinks a cell by `flexShrink × flexBasis`, so the overflow comes out of each column's
    // wrappable text (its text beyond its longest word), as a browser takes it.
    flexShrink: ((textUnits - longestWordUnits) * unitWidth) / flexBasis,
    minWidth: CELL_HORIZONTAL_INSET + longestWordUnits * unitWidth,
  } satisfies ViewStyle;
}

/** The cell font size in points; on web the variable is a `rem` length. */
function getFontSizePoints(value: string | number | undefined): number {
  if (typeof value === "number") {
    return value;
  }
  const length = Number.parseFloat(value ?? "");
  if (!Number.isFinite(length)) {
    return DEFAULT_CELL_FONT_SIZE;
  }
  return value?.endsWith("rem") === true ? length * 16 : length;
}

// Table - container for the table
export function Table({ children, components, ...props }: PropsWithChildren<TableProps>) {
  const colors = useTableColors();
  // Cell text is `text-sm`, which a slide rescales to its reader's text size.
  const unitWidth = getFontSizePoints(useCSSVariable("--text-sm")) * LATIN_LETTER_WIDTH_EM;
  const columnStyles = getTableColumnText(children).map((column) =>
    getColumnStyle(column, unitWidth),
  );
  return (
    <TableColorsContext.Provider value={colors}>
      <TableColumnStylesContext.Provider value={columnStyles}>
        <View
          {...props}
          style={[
            styles.table,
            colors.border == null ? undefined : { borderColor: colors.border },
            props.style,
          ]}
        >
          {children}
        </View>
      </TableColumnStylesContext.Provider>
    </TableColorsContext.Provider>
  );
}

/** Equal columns, for a cell outside a `Table`. */
const EVEN_CELL_STYLE = { flex: 1 } satisfies ViewStyle;

/** The flex sizing of the cell's column. */
function useCellColumnStyle(): object {
  const columnIndex = useContext(TableColumnIndexContext);
  return useContext(TableColumnStylesContext)[columnIndex] ?? EVEN_CELL_STYLE;
}

// THead - table header group
export function THead({ children, components, ...props }: PropsWithChildren<TableProps>) {
  const { headerBackground } = useContext(TableColorsContext);
  return (
    <TableHeaderContext.Provider value={true}>
      <View
        {...props}
        style={[
          headerBackground == null ? undefined : { backgroundColor: headerBackground },
          props.style,
        ]}
      >
        {children}
      </View>
    </TableHeaderContext.Provider>
  );
}

// TBody - table body group
export function TBody({ children, components, ...props }: PropsWithChildren<TableProps>) {
  return (
    <View {...props} style={props.style}>
      {children}
    </View>
  );
}

// TFoot - table footer group
export function TFoot({ children, components, ...props }: PropsWithChildren<TableProps>) {
  const { headerBackground } = useContext(TableColorsContext);
  return (
    <View
      {...props}
      style={[
        headerBackground == null ? undefined : { backgroundColor: headerBackground },
        props.style,
      ]}
    >
      {children}
    </View>
  );
}

// TR - table row
export function TR({ children, components, ...props }: PropsWithChildren<TableProps>) {
  const { divider } = useContext(TableColorsContext);
  return (
    <View
      {...props}
      style={[styles.tr, divider == null ? undefined : { borderBottomColor: divider }, props.style]}
    >
      {getRowCells(children).map((cell, columnIndex) => (
        <TableColumnIndexContext.Provider key={cell.key} value={columnIndex}>
          {cell}
        </TableColumnIndexContext.Provider>
      ))}
    </View>
  );
}

// TH - table header cell
export function TH({ children, components, style }: PropsWithChildren<CellProps>) {
  const { divider, headerBackground, headerText } = useContext(TableColorsContext);
  const columnStyle = useCellColumnStyle();
  return (
    <View
      style={[
        styles.cell,
        columnStyle,
        divider == null ? undefined : { borderRightColor: divider },
        headerBackground == null ? undefined : { backgroundColor: headerBackground },
        style,
      ]}
    >
      <CellText className={TH_TEXT_CLASS} color={headerText}>
        {children}
      </CellText>
    </View>
  );
}

// TD - table data cell
export function TD({ children, components, style }: PropsWithChildren<CellProps>) {
  const isHeader = useContext(TableHeaderContext);
  const { bodyText, divider, headerBackground, headerText } = useContext(TableColorsContext);
  const columnStyle = useCellColumnStyle();

  // A `TD` inside a `THead` picks up header styling automatically.
  return (
    <View
      style={[
        styles.cell,
        columnStyle,
        divider == null ? undefined : { borderRightColor: divider },
        isHeader && headerBackground != null ? { backgroundColor: headerBackground } : undefined,
        style,
      ]}
    >
      <CellText
        className={isHeader ? TH_TEXT_CLASS : TD_TEXT_CLASS}
        color={isHeader ? headerText : bodyText}
      >
        {children}
      </CellText>
    </View>
  );
}

// Caption - table caption
export function Caption({ children, components }: PropsWithChildren<CellProps>) {
  const { bodyText } = useContext(TableColorsContext);
  return (
    <BaconText
      selectable
      style={[styles.caption, bodyText == null ? undefined : { color: bodyText }]}
    >
      {children}
    </BaconText>
  );
}

// `@bacons/react-views`'s `Text`/`View` are typed for react-native-web (a
// `WebViewStyle`/`WebTextStyle` flavor) structurally incompatible with this
// repo's native RN style types (e.g. `animationDelay: number` vs `string`).
// These styles are plain native styles; widen the lookup so passing them to the
// Bacon components doesn't drag the web style typings into the call sites.
//
// Colors are applied at render time from the theme tokens (see `useTableColors`);
// only layout/geometry (borders widths, padding, radius) lives here.
const styles: Record<string, object> = StyleSheet.create({
  caption: {
    fontSize: 14,
    paddingVertical: 8,
    textAlign: "center",
  },
  // Widths come from the column (see `getColumnStyle`); keep `CELL_HORIZONTAL_INSET` in step.
  cell: {
    borderRightWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  table: {
    borderRadius: 2, // rounded-xs for the native-only Bacon style API.
    borderWidth: 1,
    marginVertical: 16,
    overflow: "hidden",
  },
  tr: {
    borderBottomWidth: 1,
    flexDirection: "row",
  },
});
